<script lang="ts">
	import leziciTisk from "../../assets/images/lezici-tisk-dort.svg";
	import stojiciTisk from "../../assets/images/stojici-tisk-dort.svg";
	import { maxStandingPhotos } from "../../../../core/schema/productOptionsConstants.js";
	import { inflectFit } from "./inflectFit.js";
	import { inflectStandingPhoto } from "./inflectStandingPhoto.js";
	import { inflectImageCount } from "./inflectImageCount.js";
	import ProductConfiguratorPhoto from "./ProductConfiguratorPhoto.svelte";
	import HoverTooltip from "../HoverTooltip.svelte";
	import StaticPicture from "../Picture/StaticPicture.svelte";
	import type { ProductPhoto } from "../../../../core/schema/ProductPhoto.js";
	import { createEventDispatcher } from "svelte";
	import photooncake from "../../../../core/assets/icons/photooncake.svg";
	import plus from "../../../../core/assets/icons/btn-plus.svg?raw";
	import Button from "../Button.svelte";
	import ProductOptionHeader from "./ProductOptionHeader.svelte";
	import { calculatePriceForProductOption } from "../../../../core/schema/utils/calculatePriceForProductOption.js";
	import { ProductOption } from "../../../../core/schema/ProductOption.js";
	import Message from "../Message.svelte";

	export let lyingPhoto: ProductPhoto | undefined;
	export let standingPhotos: { id: string; photo?: ProductPhoto }[];
	export let updatePhotos: (
		lyingPhoto: ProductPhoto | undefined,
		newStandingPhotos: { id: string; photo?: ProductPhoto }[],
	) => void;
	export let allowLying = false;
	export let allowStanding = false;
	export let basePrice: number;
	export let minimalPricesForOptions: Record<ProductOption, number>;
	export let changed: boolean | undefined;

	let newStandingPhotos = structuredClone(standingPhotos);

	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	const dispatch = createEventDispatcher<{ change: void }>();

	let show: "lying" | "standing" | undefined =
		lyingPhoto !== undefined ? "lying" : newStandingPhotos.length > 0 ? "standing" : undefined;

	function onChange(_standingPhotos: typeof standingPhotos, _lyingPhoto: typeof lyingPhoto): void {
		if (changed === undefined) {
			changed = false;
			return;
		}
		changed = true;
	}
	$: onChange(newStandingPhotos, lyingPhoto);

	function addStandingPhoto(): void {
		newStandingPhotos.push({
			id: crypto.randomUUID(),
		});
		newStandingPhotos = newStandingPhotos;
	}

	function changeShow(newShow: "lying" | "standing"): void {
		show = newShow;
	}

	function onPhotoChange({ detail: photo }: CustomEvent<ProductPhoto | null>, standingPhotoId?: string): void {
		if (standingPhotoId) {
			if (photo === null) {
				newStandingPhotos = newStandingPhotos.filter((standingPhoto) => standingPhoto.id !== standingPhotoId);
			} else {
				newStandingPhotos = newStandingPhotos.map((standingPhoto) => {
					if (standingPhoto.id === standingPhotoId) {
						return {
							...standingPhoto,
							photo,
						};
					}
					return standingPhoto;
				});
			}
		} else {
			lyingPhoto =
				photo !== null
					? {
							...lyingPhoto,
							...photo,
						}
					: undefined;
		}
		dispatch("change");
	}

	let uploadMap: Record<string, boolean> = {};
	function onPhotoUploading(isUploading: boolean, idForMap: string): void {
		uploadMap = {
			...uploadMap,
			[idForMap]: isUploading,
		};
	}

	function onLyingPhotoClear(): void {
		lyingPhoto = undefined;
		dispatch("change");
	}

	function onStandingPhotoClear(photo: (typeof newStandingPhotos)[number]): void {
		delete photo.photo;
		newStandingPhotos = newStandingPhotos;
		dispatch("change");
	}

	$: standingPhotosLeft = maxStandingPhotos - newStandingPhotos.length;
	$: {
		if (show === "standing" && newStandingPhotos.length === 0) {
			addStandingPhoto();
		}
	}

	// TODO Remove this after Svelte + ESLint are friends https://github.com/sveltejs/svelte-eslint-parser/issues/306#issuecomment-1938748431
	/* eslint-disable @typescript-eslint/no-unsafe-argument */

	// eslint-disable-next-line no-warning-comments
	// FIXME remove this after upgrade to Svelte 5.
	/* eslint-disable @typescript-eslint/explicit-function-return-type */

	$: hasLyingPhoto = lyingPhoto !== undefined;
	$: hasStandingPhotos = newStandingPhotos.filter(({ photo }) => photo !== undefined).length > 0;
	$: isUploading = Object.values(uploadMap).some(Boolean);
	$: minimalPrice =
		show === "standing"
			? minimalPricesForOptions[ProductOption.StandingPhotos]
			: show === "lying"
				? minimalPricesForOptions[ProductOption.LyingPhoto]
				: Math.min(
						minimalPricesForOptions[ProductOption.StandingPhotos],
						minimalPricesForOptions[ProductOption.LyingPhoto],
					);

	function calculateCurrentPrice(
		basePrice: number,
		show: "lying" | "standing" | undefined,
		newStandingPhotos: ProductPhoto[],
		newLyingPhoto: ProductPhoto | undefined,
	): number | undefined {
		let price = 0;

		if (show === "lying") {
			price += calculatePriceForProductOption(basePrice, ProductOption.LyingPhoto, newLyingPhoto);
			if (price === 0) {
				price = minimalPricesForOptions[ProductOption.LyingPhoto];
			}
		}
		if (show === "standing") {
			price += calculatePriceForProductOption(basePrice, ProductOption.StandingPhotos, newStandingPhotos);
		}

		return price;
	}

	$: currentPrice = calculateCurrentPrice(
		basePrice,
		show,
		newStandingPhotos.map(({ photo }) => photo).filter((photo) => photo !== undefined),
		lyingPhoto,
	);
</script>

<ProductOptionHeader name="Jedlý tisk" image={photooncake} price={currentPrice} {minimalPrice}>
	Nahrajte obrázky, které si přejete vytisknout. Pro nejlepší efekt volte vysokou kvalitu. Jedlý papír je bezlepkový.
</ProductOptionHeader>

<div class="flex w-full flex-col gap-4">
	<div class="relative z-[1] grid grid-cols-2 grid-rows-1 gap-2">
		<button
			type="button"
			class="group relative flex flex-col items-center justify-center gap-[.4rem] rounded-lg px-2 {show === 'lying'
				? 'border-2 border-black pb-[.5rem] pt-[.45rem] sm:p-3 sm:pb-[.4rem] sm:pt-[.7rem]'
				: 'border-1 border-gray-200 pb-[calc(.5rem+1px)] pt-[calc(.45rem+1px)] sm:px-3 sm:pb-[calc(.4rem+1px)] sm:pt-[calc(.7rem+1px)]'}"
			on:click={() => {
				changeShow("lying");
			}}
			disabled={hasStandingPhotos || isUploading || !allowLying}
		>
			<StaticPicture
				image={leziciTisk}
				alt="Ležící tisk"
				imgClass="h-[3.75rem] w-[5.9rem] sm:h-[4.5rem] sm:w-[6.625rem] {show === 'standing' || !allowLying
					? 'opacity-20'
					: 'opacity-100'}"
				width={106}
				height={72}
			/>
			<span
				class="text-xs font-semibold leading-[1.1] sm:text-base"
				class:opacity-20={show === "standing" || !allowLying}
			>
				Ležící tisk
			</span>

			{#if hasStandingPhotos && allowLying}
				<HoverTooltip>Nelze kombinovat se stojícím tiskem</HoverTooltip>
			{/if}
			{#if !allowLying}
				<HoverTooltip>U tohoto produktu nelze použít ležící tisk</HoverTooltip>
			{/if}
		</button>
		<button
			type="button"
			class="group relative flex flex-col items-center justify-center gap-[.4rem] rounded-lg {show === 'standing'
				? 'border-2 border-black pb-[.5rem] pt-[.45rem] sm:p-3 sm:pb-[.4rem] sm:pt-[.7rem]'
				: 'border-1 border-gray-200 pb-[calc(.5rem+1px)] pt-[calc(.45rem+1px)] sm:px-3 sm:pb-[calc(.4rem+1px)] sm:pt-[calc(.7rem+1px)]'}"
			on:click={() => {
				changeShow("standing");
			}}
			disabled={hasLyingPhoto || isUploading || !allowStanding}
		>
			<StaticPicture
				image={stojiciTisk}
				alt="Stojící tisk"
				imgClass="h-[3.75rem] w-[5.9rem] sm:h-[4.5rem] sm:w-[6.625rem] scale-[1.1] {show === 'lying' || !allowStanding
					? 'opacity-20'
					: 'opacity-100'}"
				width={106}
				height={72}
			/>
			<span
				class="text-xs font-semibold leading-[1.1] sm:text-base"
				class:opacity-20={show === "lying" || !allowStanding}
			>
				Stojící tisk
			</span>

			{#if hasLyingPhoto && allowStanding}
				<HoverTooltip>Nelze kombinovat s ležícím tiskem</HoverTooltip>
			{/if}
			{#if !allowStanding}
				<HoverTooltip>U tohoto produktu nelze použít stojící tisk</HoverTooltip>
			{/if}
		</button>
	</div>

	{#if show === "lying"}
		<Message variant="info" class="mb-4">
			<p class="mb-2 mt-0.5 text-xs font-medium leading-tight">
				Modrá elipsa na infografice dortu znázorňuje pouze umístění, nikoli výsledný tvar fotografie na dortu.
			</p>

			<p class="text-2xs">
				Pokud si přejete vytisknout na dort pouze ořez obrázku, upravte obrázek u sebe v PC nebo na mobilu a nahrajte.
			</p>
			<p class="text-2xs">
				V případě, že tuto úpravu nemáte možnost provést sami, nahrajte originální obrázek a
				<strong>svou představu o oříznutí obrázku nám sdělte telefonicky po dokončení objednávky</strong>, vše můžeme
				vyřídit za Vás.
			</p>
		</Message>
		<ProductConfiguratorPhoto
			bind:photo={lyingPhoto}
			on:change={onPhotoChange}
			on:clear={onLyingPhotoClear}
			on:uploading={({ detail }) => {
				onPhotoUploading(detail, "lying");
			}}
		/>
	{/if}
	{#if show === "standing"}
		<div class="bg-themeBlue/5 text-secondary mb-4 rounded-lg p-4" role="alert">
			<p class="mb-2 text-xs font-medium leading-tight">
				Modrá kolečka na infografice dortu neznázorňují výsledný tvar obrázků.
			</p>

			<p class="text-2xs">
				Pokud si přejete vytisknout na dort pouze ořezy z obrázků, upravte obrázky u sebe v PC nebo na mobilu a ty
				následně nahrajte.
			</p>
			<p class="text-2xs">
				V případě, že tyto úpravy nemáte možnost provést sami, nahrajte originální obrázky a
				<strong>svou představu o oříznutí obrázků nám sdělte telefonicky po dokončení objednávky</strong>, vše můžeme
				vyřídit za Vás.
			</p>
		</div>
		{#each newStandingPhotos as standingPhoto, index (standingPhoto.id)}
			<div class="flex flex-col gap-2 text-xs sm:text-base">
				<p class="font-semibold">{index + 1}. obrázek</p>
				<ProductConfiguratorPhoto
					bind:photo={standingPhoto.photo}
					on:change={(event) => {
						onPhotoChange(event, standingPhoto.id);
					}}
					on:clear={() => {
						onStandingPhotoClear(standingPhoto);
					}}
					on:uploading={({ detail }) => {
						onPhotoUploading(detail, standingPhoto.id);
					}}
				/>
			</div>
		{/each}
		{#if newStandingPhotos.length < maxStandingPhotos}
			<div class="col-span-2 mt-2 flex flex-col items-start gap-1">
				<span class="text-2xs leading-4">
					<!-- TODO When maximum amount of images depends on size, uncomment this -->
					Na dort <!--velikosti {productSizeNameMap[size][TENANT_LOCALE]}--> se
					{inflectFit(standingPhotosLeft)}
					ještě
					{standingPhotosLeft}
					{inflectStandingPhoto(standingPhotosLeft)}.
				</span>
				<button class="text-2xs leading-4 underline" type="button" on:click={addStandingPhoto}>
					Přidat {inflectImageCount[newStandingPhotos.length + 1]} obrázek
				</button>
			</div>
		{/if}
	{/if}
</div>

<div class="flex flex-col">
	<Button
		icon={plus}
		iconClass="w-5 h-5"
		class="mt-4 self-end"
		on:click={() => {
			updatePhotos(lyingPhoto, newStandingPhotos);
		}}
	>
		Přidat úpravu
	</Button>
</div>
